import { Plus, Trash2, Check, X as XIcon, Loader2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { supabase } from '../../../../lib/supabase';
import { useCurrentCompany } from '../../../../hooks/useCurrentCompany';

interface AgeBracket {
  id: string;
  title: string;
  min_age: number | null;
  max_age: number | null;
  all_ages: boolean | null;
}

interface BracketForm {
  title: string;
  min_age: string;
  max_age: string;
  all_ages: boolean;
}

const EMPTY: BracketForm = { title: '', min_age: '', max_age: '', all_ages: false };

export function AgeBracketsManager() {
  const { data: companyId } = useCurrentCompany();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<BracketForm>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<BracketForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['company-age-brackets', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error: qErr } = await supabase
        .from('company_age_brackets')
        .select('id, title, min_age, max_age, all_ages')
        .eq('company_id', companyId)
        .order('min_age', { ascending: true, nullsFirst: false });
      if (qErr) throw qErr;
      return (data || []) as AgeBracket[];
    },
    enabled: !!companyId,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['company-age-brackets', companyId] });

  const toPayload = (f: BracketForm) => ({
    title: f.title.trim(),
    min_age: f.all_ages || !f.min_age ? null : parseInt(f.min_age, 10),
    max_age: f.all_ages || !f.max_age ? null : parseInt(f.max_age, 10),
    all_ages: f.all_ages,
  });

  const create = useMutation({
    mutationFn: async (f: BracketForm) => {
      if (!companyId) throw new Error('No company selected');
      const { error: insErr } = await supabase
        .from('company_age_brackets')
        .insert({ company_id: companyId, ...toPayload(f) });
      if (insErr) throw insErr;
    },
    onSuccess: () => {
      setForm(EMPTY);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const update = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: BracketForm }) => {
      const { error: updErr } = await supabase
        .from('company_age_brackets')
        .update(toPayload(f))
        .eq('id', id);
      if (updErr) throw updErr;
    },
    onSuccess: () => {
      setEditingId(null);
      setEditForm(EMPTY);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error: delErr } = await supabase.from('company_age_brackets').delete().eq('id', id);
      if (delErr) throw delErr;
    },
    onSuccess: invalidate,
    onError: (e: Error) => setError(e.message),
  });

  const handleAdd = () => {
    setError(null);
    if (!form.title.trim()) return;
    create.mutate(form);
  };

  const handleDelete = (id: string, title: string) => {
    setError(null);
    if (!window.confirm(`Delete "${title}"? This will fail if any active class uses it.`)) return;
    remove.mutate(id);
  };

  return (
    <div className="max-w-2xl space-y-4">
      {/* Add row */}
      <div className="bg-[#1A1717] border border-[#504A4A] rounded-lg p-3 space-y-2">
        <p className="text-sm text-[#EBFFEE] font-medium">Add Age Bracket</p>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder='Title (e.g. "Youth 8-12")'
          className="w-full bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] placeholder-[#504A4A] focus:outline-none focus:border-[#9B1421]"
        />
        <div className="flex gap-2 items-center">
          <input
            type="number"
            disabled={form.all_ages}
            value={form.min_age}
            onChange={(e) => setForm({ ...form, min_age: e.target.value })}
            placeholder="Min age"
            className="flex-1 bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] placeholder-[#504A4A] focus:outline-none focus:border-[#9B1421] disabled:opacity-50"
          />
          <input
            type="number"
            disabled={form.all_ages}
            value={form.max_age}
            onChange={(e) => setForm({ ...form, max_age: e.target.value })}
            placeholder="Max age"
            className="flex-1 bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] placeholder-[#504A4A] focus:outline-none focus:border-[#9B1421] disabled:opacity-50"
          />
          <label className="flex items-center gap-2 text-sm text-[#A9A3A3] cursor-pointer">
            <input
              type="checkbox"
              checked={form.all_ages}
              onChange={(e) => setForm({ ...form, all_ages: e.target.checked })}
            />
            All ages
          </label>
        </div>
        <button
          onClick={handleAdd}
          disabled={create.isPending || !form.title.trim()}
          className="w-full flex items-center justify-center gap-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
        >
          {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Add Bracket
        </button>
      </div>

      {error && (
        <div className="text-red-400 text-sm bg-red-900/20 border border-red-600/40 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="border border-[#363232] rounded-lg overflow-hidden">
        {query.isLoading ? (
          <div className="p-6 flex justify-center">
            <Loader2 size={20} className="animate-spin text-[#A9A3A3]" />
          </div>
        ) : query.data?.length === 0 ? (
          <p className="p-4 text-sm text-[#A9A3A3] text-center">No age brackets yet.</p>
        ) : (
          query.data?.map((a) => {
            const isEditing = editingId === a.id;
            return (
              <div
                key={a.id}
                className="flex items-center justify-between px-3 py-2 border-b border-[#363232] last:border-0 gap-2"
              >
                {isEditing ? (
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="w-full bg-[#111010] border border-[#504A4A] rounded px-2 py-1 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                    />
                    <div className="flex gap-2 items-center">
                      <input
                        type="number"
                        disabled={editForm.all_ages}
                        value={editForm.min_age}
                        onChange={(e) => setEditForm({ ...editForm, min_age: e.target.value })}
                        placeholder="Min"
                        className="flex-1 bg-[#111010] border border-[#504A4A] rounded px-2 py-1 text-sm text-[#EBFFEE] disabled:opacity-50"
                      />
                      <input
                        type="number"
                        disabled={editForm.all_ages}
                        value={editForm.max_age}
                        onChange={(e) => setEditForm({ ...editForm, max_age: e.target.value })}
                        placeholder="Max"
                        className="flex-1 bg-[#111010] border border-[#504A4A] rounded px-2 py-1 text-sm text-[#EBFFEE] disabled:opacity-50"
                      />
                      <label className="text-xs text-[#A9A3A3] flex gap-1 items-center">
                        <input
                          type="checkbox"
                          checked={editForm.all_ages}
                          onChange={(e) => setEditForm({ ...editForm, all_ages: e.target.checked })}
                        />
                        All
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#EBFFEE] font-medium">{a.title}</p>
                    <p className="text-xs text-[#A9A3A3]">
                      {a.all_ages
                        ? 'All ages'
                        : a.min_age !== null && a.max_age !== null
                          ? `${a.min_age} – ${a.max_age}`
                          : a.min_age !== null
                            ? `${a.min_age}+`
                            : a.max_age !== null
                              ? `Up to ${a.max_age}`
                              : '—'}
                    </p>
                  </div>
                )}

                <div className="flex items-center gap-1 shrink-0">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => update.mutate({ id: a.id, f: editForm })}
                        disabled={update.isPending || !editForm.title.trim()}
                        className="p-1.5 text-green-400 hover:bg-green-900/20 rounded"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        onClick={() => {
                          setEditingId(null);
                          setEditForm(EMPTY);
                        }}
                        className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded"
                      >
                        <XIcon size={14} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setEditingId(a.id);
                          setEditForm({
                            title: a.title,
                            min_age: a.min_age?.toString() ?? '',
                            max_age: a.max_age?.toString() ?? '',
                            all_ages: !!a.all_ages,
                          });
                        }}
                        className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(a.id, a.title)}
                        disabled={remove.isPending}
                        className="p-1.5 text-[#A9A3A3] hover:text-red-400 hover:bg-red-900/20 rounded disabled:opacity-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
